import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { VersionRange } from '../core/version-range.js';
import { Language } from './language.js';
import { LocalizedString } from './localized-string.js';

export class AddressComponent extends DataModel {
  static readonly TYPE_ID: string = 'dd0a8327277f4f1f99b34367f05a3cce';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly names: LocalizedString,
  ) {
    super();
  }

  get $typeId() {
    return AddressComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(AddressComponent.SCHEMA_VERSION);
  }

  // For backward compatibility
  get name(): string {
    return this.names.getAnyOrEmpty();
  }

  static fromMap(context: SerializationContext, map: SerializedData): AddressComponent {
    return new AddressComponent(
        MapUtils.get<string>(map, 'uniqueId'),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'names')),
    );
  }

  static fromName(name: string, language: Language = Language.Hungarian): AddressComponent {
    return new AddressComponent(
        name.toLowerCase().replace(' ', '_'),
        LocalizedString.forLanguage(language, name),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'names': context.toData(this.names),
    };
  }

  clone(): this {
    return new AddressComponent(
        this.uniqueId,
        this.names.clone(),
    ) as this;
  }

  isSame(other: AddressComponent): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        isSameData(other.names, this.names);
  }
}

export class AddressComponentBuilder extends DataModelBuilder<AddressComponent> {
  build(context: SerializationContext, map: SerializedData): AddressComponent {
    return AddressComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(AddressComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(AddressComponent.SCHEMA_VERSION)) {
      return null;
    }

    let version = versionRange.to;

    // In this version we replace the `name` field of type `string` with the `names` field of type `LocalizedString`.
    if(version < 2) {
      const name = MapUtils.get<string>(map, 'name');
      map['names'] = context.toData(LocalizedString.forLanguage(Language.Hungarian, name));
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(context: SerializationContext, model: AddressComponent, map: SerializedData): SerializedData {
    map['name'] = model.name;
    map['$schema_version'] = VersionRange.encodedBetween(1, AddressComponent.SCHEMA_VERSION);
    return map;
  }
}
